import React from "react";
import { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { GlobalSurveyTemplate, SurveySegmentation, SurveyTheme, fieldValidation } from "../../amorg-common";
import { editGlobalSurveyTemplate, getGlobalSurveyTemplates } from "../../api/adminClientApi";
import { LoadingPageOverlay } from "../../designSystem/LoadingPageOverlay";
import { PageWrapper } from "./_components/PageWrapper";
import { LinkButton } from "../../designSystem/button/LinkButton";
import { TextAreaField } from "../../designSystem/formField/textArea/TextAreaField";
import { CheckboxField } from "../../designSystem/formField/CheckboxField";
import { SelectField } from "../../designSystem/formField/SelectField";
import { StaticTextField } from "../../designSystem/formField/StaticTextField";
import { ButtonRow } from "../../designSystem/button/ButtonRow";
import { Button } from "../../designSystem/button/Button";
import { surveyValidation } from "../../amorg-common/modules/fieldValidation/fields/surveyFieldValidation";
import { AmorgAdminPageProvider } from "./AmorgAdminPageContext";

export function AmorgAdminEditTemplate (): JSX.Element
{
    const params = useParams<{ templateId: string; }>();
    const history = useHistory();

    const [loading, setLoading] = React.useState(true);
    const [template, setTemplate] = React.useState<GlobalSurveyTemplate>(null);
    const [, setValidSegmentations] = React.useState(true);
    const [, setValidThemes] = React.useState(true);

    useEffect(() =>
    {
        const init = async () =>
        {
            setTemplate(
                await getTemplate()
            );
            setLoading(false);
        };
        init().catch(console.error);
    }, []);

    async function getTemplate ()
    {
        return (await getGlobalSurveyTemplates(() => history.push(`/amorgy/login`)))
            .find(t => t.id === params.templateId);
    }

    async function handleSave ()
    {
        const result = await editGlobalSurveyTemplate(template, () => history.push(`/amorgy/login`));
        if (result)
            history.push('/amorgy/globaltemplates');
        else
            alert('fejl - kunne ikke gennemføre opdateringen');
    }

    const valid = fieldValidation.surveyTemplate.globalTemplate(template) === 'ok';

    return (
        <AmorgAdminPageProvider>
            <PageWrapper>
                <LinkButton icon="arrows_chevron_chevronBack" text="tilbage" href="/amorgy/globaltemplates" variant={"text"} />

                <LoadingPageOverlay loading={loading} />
                {template &&
                    <>
                        <ButtonRow variant="right-aligned">
                            <Button variant="filled" text="Gem" disabled={!valid} onClick={handleSave} />
                        </ButtonRow>
                        <StaticTextField
                            label="id"
                            staticText={template.id}
                        />
                        <CheckboxField
                            label="Aktiv"
                            text=""
                            value="enabled"
                            checked={template.enabled}
                            onChange={(value, checked) => setTemplate({ ...template, enabled: checked })}
                        />
                        <SelectField
                            label={"Kategory"}
                            selectedValue={template.category}
                            options={[
                                { text: 'global', value: 'global' }
                            ]}
                            onChange={value => setTemplate({ ...template, category: value })}
                        />
                        <TextAreaField
                            value={template.description}
                            onChange={value => setTemplate({ ...template, description: value })}
                            label="Beskrivelse af skabelon"
                            validator={value => fieldValidation.surveyTemplate.description(value) ? { valid: true } : { valid: false, errorMessage: '' }}
                        />
                        <TextAreaField
                            value={template.title}
                            onChange={value => setTemplate({ ...template, title: value })}
                            label="Titel"
                            validator={value => fieldValidation.surveyTemplate.title(value) ? { valid: true } : { valid: false, errorMessage: '' }}
                        />
                        <TextAreaField
                            value={template.introduction.text}
                            onChange={value => setTemplate({
                                ...template, introduction: {
                                    ...template.introduction, text: value
                                }
                            })}
                            label="Introduktion"
                            validator={value => fieldValidation.survey.introductionText(value) ? { valid: true } : { valid: false, errorMessage: '' }}
                        />
                        <JsonEditor
                            label="Segmenteringer"
                            jsonObject={template.segmentations}
                            onChange={(value, valid) =>
                            {
                                setValidSegmentations(valid);
                                if (valid)
                                    setTemplate({ ...template, segmentations: JSON.parse(value) });
                            }}
                            validator={
                                (value) =>
                                {
                                    const valid = surveyValidation.surveySegmentation.validateSurveySegmentationList(template.translationLanguages, value as SurveySegmentation[]);
                                    return valid
                                        ? { valid: true }
                                        : { valid: false, errorMessage: 'Ugyldig struktur' };
                                }} />

                        <JsonEditor
                            label="Temaer og spørgsmål"
                            jsonObject={template.themes}
                            onChange={(value, valid) =>
                            {
                                setValidThemes(valid);
                                if (valid)
                                    setTemplate({ ...template, themes: JSON.parse(value) });
                            }}
                            validator={
                                (value) =>
                                {
                                    const valid = fieldValidation.surveyTemplate.themes(value as SurveyTheme[]);
                                    return valid
                                        ? { valid: true }
                                        : { valid: false, errorMessage: 'Ugyldig struktur' };
                                }} />
                    </>
                }
            </PageWrapper>
        </AmorgAdminPageProvider>
    );
}

type JsonEditorProps = {
    label: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jsonObject: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (value: any, valid: boolean) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validator: (value: any) => { valid: true; } | { valid: false, errorMessage: string; };
};
function JsonEditor (props: JsonEditorProps): JSX.Element
{
    const [jsonString, setJsonString] = React.useState('');

    React.useEffect(() =>
    {
        const jsonString = JSON.stringify(props.jsonObject, undefined, 2);
        setJsonString(jsonString);
    }, [props.jsonObject]);

    function handleChange (value: string)
    {
        const isValid = validate(value).valid;
        setJsonString(value);
        props.onChange(value, isValid);
    }

    function validate (value: string): { valid: true; } | { valid: false, errorMessage: string; }
    {
        try
        {
            const jsonObject = JSON.parse(value);
            const result = props.validator(jsonObject);
            return result;
        }
        catch (e)
        {
            return { valid: false, errorMessage: e.message };
        }
    }

    return (
        <TextAreaField
            value={jsonString}
            onChange={handleChange}
            label={props.label}
            validator={validate}
        />
    );
}